/**
 * R-ROADMAP-INC — roadmap (RecommendationGraph) export.
 *
 * Word (.docx) + print export for a site's recommendation roadmap. Reuses the
 * shared DOCX utilities under `src/lib/export/docx/*`.
 *
 * Export formats:
 *   - Word (.docx)         — buildRoadmapDocx / buildRoadmapDocxBlob   [SHIPPED]
 *   - Print (HTML)         — roadmapPrintHtml / printRoadmap           [SHIPPED]
 *   - PowerPoint (.pptx)   — buildRoadmapPptx / buildRoadmapPptxBlob   [SHIPPED]
 *
 * PowerPoint note: the `pptxgenjs` dependency was approved (see the report
 * PPTX export decision). `buildRoadmapPptx(graph, site, opts)` builds one
 * slide per phase (each with a recommendations table) and is wired into the
 * roadmap Export menu as a "PowerPoint" item alongside Word + Print.
 *
 * Bundle discipline: `pptxgenjs` is imported ONLY via a dynamic
 * `await import('pptxgenjs')` inside `buildRoadmapPptx` (mirrors the
 * html2canvas / file-saver convention) so it never lands in the main bundle.
 */
import {
  Document,
  Packer,
  Footer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
  AlignmentType,
  PageNumber,
  type ISectionOptions,
} from 'docx';
import type { SiteEntity } from '../../types/site';
import type {
  RecommendationGraph,
  RecommendationNode,
  RecommendationPhase,
} from '../../types/recommendationGraph';
import { PHASE_ORDER } from '../audit/recommendationGraphBuilder';
import {
  normalizeRecommendationStatus,
  recommendationStatusLabel,
} from '../audit/recommendationStatus';
import { DOCX_COLORS, DOCX_FONT, DOCX_SIZES } from './docx/docxStyles';
import {
  bodyParagraph,
  coverLine,
  coverPlatformCreditLine,
  heading1,
  pageBreakParagraph,
  slugifyFilenamePart,
} from './docx/docxParagraph';
import {
  GATEWAY_POWER_NAME,
  REPORT_PLATFORM_CREDIT_LINE,
  gatewayPowerMarkDataUri,
  gatewayPowerMarkLightPptxData,
} from '../reportBrand/gatewayPowerReportBrand';

const PHASE_LABELS: Record<RecommendationPhase, string> = {
  immediate: 'Immediate',
  '30d': '30 days',
  '90d': '90 days',
  '6mo': '6 months',
  '12mo+': '12 months+',
  unscheduled: 'Unscheduled',
};

export interface RoadmapExportOptions {
  preparedBy?: string;
  /** Fixed ISO timestamp for deterministic cover/footer (tests). */
  dateOverride?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared data shaping
// ─────────────────────────────────────────────────────────────────────────────

interface RoadmapTableRow {
  recommendation: string;
  status: string;
  owner: string;
  deadline: string;
  prerequisites: string;
}

/** Group nodes by phase in canonical PHASE_ORDER, dropping empty phases. */
export function groupNodesByPhase(
  graph: RecommendationGraph,
): Array<{ phase: RecommendationPhase; label: string; nodes: RecommendationNode[] }> {
  const byPhase = new Map<RecommendationPhase, RecommendationNode[]>();
  for (const node of graph.nodes) {
    const list = byPhase.get(node.phase) ?? [];
    list.push(node);
    byPhase.set(node.phase, list);
  }
  return PHASE_ORDER
    .filter((phase) => (byPhase.get(phase)?.length ?? 0) > 0)
    .map((phase) => ({
      phase,
      label: PHASE_LABELS[phase],
      nodes: byPhase.get(phase) ?? [],
    }));
}

/** Resolve prerequisite node ids to a human-readable, comma-joined string. */
function prerequisiteLabels(node: RecommendationNode, graph: RecommendationGraph): string {
  const prereqIds = node.prerequisites?.length
    ? node.prerequisites
    : graph.edges.filter((e) => e.target === node.id).map((e) => e.source);
  const unique = [...new Set(prereqIds)];
  if (unique.length === 0) return '–';
  return unique
    .map((id) => graph.nodes.find((n) => n.id === id)?.canonicalForm ?? id)
    .join('; ');
}

function rowFromNode(node: RecommendationNode, graph: RecommendationGraph): RoadmapTableRow {
  return {
    recommendation: node.canonicalForm,
    status: recommendationStatusLabel(normalizeRecommendationStatus(node.status)),
    owner: node.owner?.trim() || '–',
    deadline: node.deadline?.trim() || '–',
    prerequisites: prerequisiteLabels(node, graph),
  };
}

function isoDay(opts: RoadmapExportOptions): string {
  return opts.dateOverride?.slice(0, 10) ?? new Date().toISOString().slice(0, 10);
}

function formatCoverDate(isoDayStr: string): string {
  return new Date(`${isoDayStr}T12:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Word (.docx)
// ─────────────────────────────────────────────────────────────────────────────

const TABLE_BORDERS = {
  top: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
  left: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
  right: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
  insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
  insideVertical: { style: BorderStyle.SINGLE, size: 1, color: DOCX_COLORS.border },
};

function headerCell(text: string): TableCell {
  return new TableCell({
    shading: { type: ShadingType.CLEAR, fill: DOCX_COLORS.tableHeader },
    children: [new Paragraph({
      children: [new TextRun({ text, font: DOCX_FONT, size: DOCX_SIZES.small, bold: true })],
    })],
  });
}

function bodyCell(text: string): TableCell {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text, font: DOCX_FONT, size: DOCX_SIZES.small, color: DOCX_COLORS.textPrimary })],
    })],
  });
}

function phaseTable(rows: RoadmapTableRow[]): Table {
  const tableRows = [
    new TableRow({
      children: [
        headerCell('Recommendation'),
        headerCell('Status'),
        headerCell('Owner'),
        headerCell('Deadline'),
        headerCell('Prerequisites'),
      ],
      tableHeader: true,
    }),
    ...rows.map((r) => new TableRow({
      children: [
        bodyCell(r.recommendation),
        bodyCell(r.status),
        bodyCell(r.owner),
        bodyCell(r.deadline),
        bodyCell(r.prerequisites),
      ],
    })),
  ];
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: TABLE_BORDERS,
    rows: tableRows,
  });
}

function roadmapFooter(): Footer {
  return new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({ text: 'Datacrate Roadmap | Page ', font: DOCX_FONT, size: DOCX_SIZES.small, color: DOCX_COLORS.textMuted }),
          new TextRun({ children: [PageNumber.CURRENT], font: DOCX_FONT, size: DOCX_SIZES.small }),
          new TextRun({ text: ' of ', font: DOCX_FONT, size: DOCX_SIZES.small, color: DOCX_COLORS.textMuted }),
          new TextRun({ children: [PageNumber.TOTAL_PAGES], font: DOCX_FONT, size: DOCX_SIZES.small }),
        ],
      }),
    ],
  });
}

/** Build a Word document for the roadmap: cover + per-phase tables. */
export async function buildRoadmapDocx(
  graph: RecommendationGraph,
  site: Pick<SiteEntity, 'id' | 'name'>,
  opts: RoadmapExportOptions = {},
): Promise<Buffer> {
  const day = isoDay(opts);
  const phases = groupNodesByPhase(graph);

  type SectionChild = Paragraph | Table;
  const children: SectionChild[] = [
    coverLine(site.name || 'Site', { bold: true, size: DOCX_SIZES.coverTitle, color: DOCX_COLORS.brand }),
    coverLine('Development Roadmap'),
    coverLine(`${graph.nodes.length} recommendations · ${phases.length} phases`),
    coverLine(`Date: ${formatCoverDate(day)}`),
    coverLine(`Prepared by: ${opts.preparedBy ?? 'Datacrate'}`),
    // Gateway Power platform credit — the cover's last, quietest line.
    coverPlatformCreditLine(),
    pageBreakParagraph(),
  ];

  if (phases.length === 0) {
    children.push(heading1('Roadmap'));
    children.push(bodyParagraph('No recommendations have been extracted for this site yet.'));
  } else {
    for (const phase of phases) {
      children.push(heading1(`${phase.label} (${phase.nodes.length})`));
      children.push(phaseTable(phase.nodes.map((n) => rowFromNode(n, graph))));
    }
  }

  const doc = new Document({
    creator: opts.preparedBy ?? 'Datacrate',
    title: `${site.name || 'Site'} – Development Roadmap`,
    description: `Datacrate roadmap export | siteId=${site.id}`,
    sections: [{
      footers: { default: roadmapFooter() },
      children,
    } satisfies ISectionOptions],
  });

  const buf = await Packer.toArrayBuffer(doc);
  return typeof Buffer !== 'undefined'
    ? Buffer.from(buf)
    : (new Uint8Array(buf) as unknown as Buffer);
}

export function roadmapDocxFilename(site: Pick<SiteEntity, 'name'>, isoDayStr: string): string {
  return `${slugifyFilenamePart(site.name || 'site')}-roadmap-${isoDayStr}.docx`;
}

/** Browser download helper — returns a Blob for anchor.click(). */
export async function buildRoadmapDocxBlob(
  graph: RecommendationGraph,
  site: Pick<SiteEntity, 'id' | 'name'>,
  opts: RoadmapExportOptions = {},
): Promise<Blob> {
  const buffer = await buildRoadmapDocx(graph, site, opts);
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PowerPoint (.pptx)
// ─────────────────────────────────────────────────────────────────────────────

// pptxgenjs hex colors are 6-char WITHOUT a leading '#'.
const PPTX_BRAND = '0071DF';
const PPTX_DARK = '1F1F1F';
const PPTX_GRAY = '6B7280';
const PPTX_LIGHT_GRAY = 'F3F4F6';
const PPTX_WHITE = 'FFFFFF';
const PPTX_FOOTER = 'Datacrate · CONFIDENTIAL';

/**
 * Build a PowerPoint deck for the roadmap: a title slide + one slide per
 * phase. Each phase slide carries the same Recommendation/Status/Owner/
 * Deadline/Prerequisites table the Word + Print exports render (reuses
 * `groupNodesByPhase` + `rowFromNode`).
 *
 * Bundle discipline — `pptxgenjs` is dynamic-imported here only.
 */
export async function buildRoadmapPptx(
  graph: RecommendationGraph,
  site: Pick<SiteEntity, 'id' | 'name'>,
  opts: RoadmapExportOptions = {},
): Promise<Blob> {
  const PptxGenJS = (await import('pptxgenjs')).default;
  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE'; // 13.33" x 7.5"
  pres.author = opts.preparedBy ?? 'Datacrate';
  pres.company = 'Datacrate';
  pres.title = `${site.name || 'Site'} – Development Roadmap`;
  pres.subject = `Datacrate roadmap export | siteId=${site.id}`;

  const CONTENT_W = 12.13; // 13.33 - 2 * 0.6 margin
  const day = isoDay(opts);
  const phases = groupNodesByPhase(graph);

  const footer = (slide: ReturnType<typeof pres.addSlide>): void => {
    slide.addText(PPTX_FOOTER, {
      x: 0.6, y: 7.05, w: CONTENT_W, h: 0.3,
      fontSize: 9, color: PPTX_GRAY, align: 'left', fontFace: 'Arial',
    });
  };

  // ── Title slide ──
  {
    const slide = pres.addSlide();
    slide.background = { color: PPTX_WHITE };
    slide.addText((site.name || 'Site').toUpperCase(), {
      x: 0.6, y: 2.0, w: CONTENT_W, h: 1.0,
      fontSize: 40, bold: true, color: PPTX_BRAND, fontFace: 'Arial',
    });
    slide.addText('Development Roadmap', {
      x: 0.6, y: 3.1, w: CONTENT_W, h: 0.7,
      fontSize: 24, color: PPTX_DARK, fontFace: 'Arial',
    });
    slide.addText(
      `${graph.nodes.length} recommendations · ${phases.length} phases · ${formatCoverDate(day)}`,
      { x: 0.6, y: 4.0, w: CONTENT_W, h: 0.4, fontSize: 14, color: PPTX_GRAY, fontFace: 'Arial' },
    );
    slide.addText(`Prepared by: ${opts.preparedBy ?? 'Datacrate'}`, {
      x: 0.6, y: 4.5, w: CONTENT_W, h: 0.4, fontSize: 12, color: PPTX_GRAY, fontFace: 'Arial',
    });
    // Gateway Power platform credit — the small G mark (light artwork: the
    // title slide is white) + the shared credit line, footer-sized and grey,
    // under the "Prepared by" line (ends 4.9") and well above the footer
    // (7.05"). Title slide only, like the site-report deck's credit. The text
    // boxes above carry pptxgenjs' default ~0.1" inset, so the mark starts on
    // the same visual column as their text.
    const CREDIT_Y = 5.0;
    const CREDIT_H = 0.27;
    const CREDIT_MARK = 0.15;
    const CREDIT_X = 0.6 + 0.1;
    slide.addImage({
      data: gatewayPowerMarkLightPptxData(),
      x: CREDIT_X,
      y: CREDIT_Y + (CREDIT_H - CREDIT_MARK) / 2,
      w: CREDIT_MARK,
      h: CREDIT_MARK,
      altText: `${GATEWAY_POWER_NAME} logo mark`,
    });
    slide.addText(REPORT_PLATFORM_CREDIT_LINE, {
      x: CREDIT_X + CREDIT_MARK + 0.07, y: CREDIT_Y, w: 5.5, h: CREDIT_H,
      fontSize: 9, color: PPTX_GRAY, fontFace: 'Arial', valign: 'middle', margin: 0,
    });
    footer(slide);
  }

  // ── One slide per phase ──
  if (phases.length === 0) {
    const slide = pres.addSlide();
    slide.background = { color: PPTX_WHITE };
    slide.addText('Roadmap', { x: 0.6, y: 0.5, w: CONTENT_W, h: 0.6, fontSize: 26, bold: true, color: PPTX_BRAND, fontFace: 'Arial' });
    slide.addText('No recommendations have been extracted for this site yet.', {
      x: 0.6, y: 1.4, w: CONTENT_W, h: 0.6, fontSize: 16, color: PPTX_DARK, fontFace: 'Arial',
    });
    footer(slide);
  } else {
    const headerCells = ['Recommendation', 'Status', 'Owner', 'Deadline', 'Prerequisites'].map(label => ({
      text: label,
      options: { bold: true, color: PPTX_WHITE, fill: { color: PPTX_BRAND }, fontSize: 12, align: 'left' as const },
    }));
    for (const phase of phases) {
      const slide = pres.addSlide();
      slide.background = { color: PPTX_WHITE };
      slide.addShape(pres.ShapeType.rect, { x: 0.6, y: 0.55, w: 0.14, h: 0.5, fill: { color: PPTX_BRAND } });
      slide.addText(`${phase.label} (${phase.nodes.length})`, {
        x: 0.85, y: 0.5, w: CONTENT_W - 0.25, h: 0.6,
        fontSize: 26, bold: true, color: PPTX_BRAND, fontFace: 'Arial',
      });
      const bodyRows = phase.nodes.map((node) => {
        const r = rowFromNode(node, graph);
        return [r.recommendation, r.status, r.owner, r.deadline, r.prerequisites].map(text => ({
          text, options: { color: PPTX_DARK, fontSize: 11 },
        }));
      });
      slide.addTable([headerCells, ...bodyRows], {
        x: 0.6, y: 1.5, w: CONTENT_W,
        colW: [4.5, 1.6, 1.9, 1.9, 2.23],
        border: { type: 'solid', color: PPTX_LIGHT_GRAY, pt: 1 },
        margin: 4, fontFace: 'Arial', valign: 'top', autoPage: true, autoPageRepeatHeader: true,
      });
      footer(slide);
    }
  }

  const out = await pres.write({ outputType: 'blob' });
  return out as Blob;
}

/** Suggested download filename for a roadmap PPTX. */
export function roadmapPptxFilename(site: Pick<SiteEntity, 'name'>, isoDayStr: string): string {
  return `${slugifyFilenamePart(site.name || 'site')}-roadmap-${isoDayStr}.pptx`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Print (HTML)
// ─────────────────────────────────────────────────────────────────────────────

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Build a print-friendly HTML document for the roadmap. */
export function roadmapPrintHtml(
  graph: RecommendationGraph,
  site: Pick<SiteEntity, 'name'>,
  opts: RoadmapExportOptions = {},
): string {
  const day = isoDay(opts);
  const phases = groupNodesByPhase(graph);
  const title = `${site.name || 'Site'} – Development Roadmap`;

  const phaseSections = phases.length === 0
    ? '<p>No recommendations have been extracted for this site yet.</p>'
    : phases.map((phase) => {
      const rows = phase.nodes.map((node) => {
        const r = rowFromNode(node, graph);
        return `<tr>
          <td>${escapeHtml(r.recommendation)}</td>
          <td>${escapeHtml(r.status)}</td>
          <td>${escapeHtml(r.owner)}</td>
          <td>${escapeHtml(r.deadline)}</td>
          <td>${escapeHtml(r.prerequisites)}</td>
        </tr>`;
      }).join('');
      return `<section>
        <h2>${escapeHtml(phase.label)} (${phase.nodes.length})</h2>
        <table>
          <thead><tr><th>Recommendation</th><th>Status</th><th>Owner</th><th>Deadline</th><th>Prerequisites</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
    }).join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: Calibri, Aptos, Arial, sans-serif; color: #1f2937; margin: 32px; }
  h1 { color: #0071df; font-size: 24px; margin-bottom: 4px; }
  .meta { color: #6b7280; font-size: 13px; margin-bottom: 24px; }
  h2 { font-size: 16px; margin-top: 28px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; }
  .platform-credit { display: flex; align-items: center; gap: 5px; margin-top: 6px; font-size: 11px; }
  .platform-credit img { width: 10px; height: 10px; flex: none; }
  @media print { body { margin: 0; } section { page-break-inside: avoid; } }
</style>
</head>
<body>
  <h1>${escapeHtml(site.name || 'Site')}</h1>
  <div class="meta">Development Roadmap · ${graph.nodes.length} recommendations · ${formatCoverDate(day)}<span class="platform-credit" data-report-platform-credit="gateway-power"><img src="${gatewayPowerMarkDataUri('on-light')}" alt="" aria-hidden="true" width="10" height="10">${escapeHtml(REPORT_PLATFORM_CREDIT_LINE)}</span></div>
  ${phaseSections}
</body>
</html>`;
}

/** Open the roadmap in a new window and trigger the browser print dialog. */
export function printRoadmap(
  graph: RecommendationGraph,
  site: Pick<SiteEntity, 'name'>,
  opts: RoadmapExportOptions = {},
): void {
  if (typeof window === 'undefined') return;
  const html = roadmapPrintHtml(graph, site, opts);
  // NB: do NOT pass `noopener`/`noreferrer` here — per spec, `window.open` with
  // `noopener` returns `null`, which silently broke Print (we need the window
  // handle to write the document into). The open is user-gesture-initiated, so
  // it is allowed by pop-up policy; surface a clear error if it is still blocked.
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Could not open a print window – please allow pop-ups for this site, then try again.');
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Defer print so the new window finishes layout first.
  win.setTimeout(() => win.print(), 250);
}
